import React, { useCallback, useMemo, useState } from "react";
import Client from "../../models/client";
import useSWR from "swr";
import { createPageGroup, deletePageGroup, FETCH_KEYS, fetcherWithAuth } from "../api";
import BigSpinning from "./BigSpinning";
import { findSwitchoverPairs, isRegularPage, PageGroupData, SwitchoverPair } from "../utils/switchoverCheck";
import { ActionIcon, Badge, Box, Button, Group, ScrollArea, Stack, Table, Tabs, Text, TextInput, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { FilesIcon, MagnifyingGlassIcon, PencilSimpleIcon, PlayCircleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import SwitchoverSummary from "../components/SwitchoverSummary";
import { formatDate } from "../utils/dateFormat";
import { BaseModal } from "../components/BaseModal";
import { useDisclosure } from "@mantine/hooks";
import PageGroupFormModal from "../components/PageGroupFormModal";
import { notifications } from "@mantine/notifications";

const TAB_NAMES = ['invite', 'invite2', 'guest'] as const
type PageGroupName = typeof TAB_NAMES[number]

interface Props {
  client: Client
}

const PageGroupsContainer: React.FC<Props> = ({ client }) => {
  const { data, error, mutate } = useSWR(FETCH_KEYS.pageGroups(client.id), fetcherWithAuth)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [pageGroup, setPageGroup] = useState<PageGroupData | null>(null)
  const [openedForm, formModal] = useDisclosure(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<PageGroupName>('invite')
  const [searchQuery, setSearchQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleClickEdit = (row: PageGroupData) => {
    if (
      row.enabled &&
      !window.confirm('公開中のページを編集します。保存すると本番に反映されます。よろしいですか？')
    ) {
      return
    }
    setEditingId(row.id)
    formModal.open()
  }

  const handleClickAddButton = async () => {
    if (creating) return
    setCreating(true)
    try {
      const created = await createPageGroup(client.id, activeTab, 0)
      setEditingId(created.id)
      formModal.open()
      mutate()
    } catch (e) {
      notifications.show({ message: 'ページの作成に失敗しました', color: 'red' })
    } finally {
      setCreating(false)
    }
  }
  const handleClickDeleteButton = (id: number) => {
    const target = data?.page_groups.find((item: PageGroupData) => item.id === id) || null
    setIsDialogOpen(true)
    setPageGroup(target)
  }

  const handleDeletePageGroup = async () => {
    if (!pageGroup) return
    if (deleting) return
    setDeleting(true)
    try {
      await deletePageGroup(client.id, pageGroup.id)
      setIsDialogOpen(false)
      mutate()
      notifications.show({ message: 'ページを削除しました', color: 'teal' })
    } catch (e) {
      notifications.show({ message: 'ページの削除に失敗しました', color: 'red' })
    } finally {
      setDeleting(false)
    }
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
  }

  const items: PageGroupData[] = data?.page_groups ?? []
  const switchoverPairs = useMemo(() => findSwitchoverPairs(items), [items])
  const regularPages = useMemo(() => items.filter((row) => isRegularPage(row)), [items])

  const matchesSearch = useCallback((page: PageGroupData) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      String(page.id).includes(q) ||
      (page.page_description ?? '').toLowerCase().includes(q)
    )
  }, [searchQuery])

  const pagesByTab = useMemo(() => {
    return TAB_NAMES.reduce((acc, name) => {
      acc[name] = regularPages.filter((p) => p.name === name)
      return acc
    }, {} as Record<PageGroupName, PageGroupData[]>)
  }, [regularPages])

  const pairsByTab = useMemo(() => {
    return TAB_NAMES.reduce((acc, name) => {
      acc[name] = switchoverPairs.filter((p) => p.name === name)
      return acc
    }, {} as Record<PageGroupName, SwitchoverPair[]>)
  }, [switchoverPairs])

  const filteredByTab = useMemo(() => {
    return TAB_NAMES.reduce((acc, name) => {
      acc[name] = pagesByTab[name].filter(matchesSearch)
      return acc
    }, {} as Record<PageGroupName, PageGroupData[]>)
  }, [pagesByTab, matchesSearch])

  if (error) return <div>Failed to load</div>
  if (!data) return <BigSpinning />

  return (
    <Box>
      <Group gap={12} mb={24}>
        <ThemeIcon variant="transparent" color="#5DA327">
          <FilesIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          ページ一覧
        </Title>
      </Group>

      <Group justify="space-between">
        <Box>
          <Text fz={14} c="#000000CC">
            ※ プレビューページのURLはユーザーにはお知らせしないでください。
          </Text>
          <Text fz={14} c="#000000CC">
            ※ 本番設定が複数ある場合はIDが若いものが優先されます。
          </Text>
          <Text fz={14} c="#000000CC">
            ※ ユーザーには紹介者用（invite）ページを告知してください。友人用（guest）ページのURLを直接伝えると正しい計測ができません。
          </Text>
        </Box>

        <TextInput
          placeholder="ID・概要で検索"
          leftSection={<MagnifyingGlassIcon size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          mt={24}
          w={400}
        />
      </Group>

      <Tabs
        value={activeTab}
        onChange={(v) => v && setActiveTab(v as PageGroupName)}
        mt={40}
      >
        <Tabs.List>
          {TAB_NAMES.map((name) => {
            const hitCount = filteredByTab[name].length
            return (
              <Tabs.Tab
                key={name}
                value={name}
                style={{ color: activeTab === name ? 'var(--mantine-color-invy-6)' : undefined }}
                rightSection={
                  searchQuery ? (
                    <Badge size="sm" variant="light" color={hitCount === 0 ? 'gray' : 'red'}>{hitCount}</Badge>
                  ) : null
                }
              >
                {name}
              </Tabs.Tab>
            )
          })}
        </Tabs.List>

        {TAB_NAMES.map((name) => {
          const pagesInTab = pagesByTab[name]
          const filteredPairs = pairsByTab[name]
          const filteredPages = filteredByTab[name]

          return (
            <Tabs.Panel
              key={name}
              value={name}
              p="md"
            >
              <SwitchoverSummary
                pairs={filteredPairs}
                allPages={pagesInTab}
                onUpdate={() => mutate()}
              />

              <Button
                variant="filled"
                radius="xl"
                leftSection={<PlusIcon size={16} weight="bold" />}
                onClick={handleClickAddButton}
                loading={creating}
                mt={24}
              >
                新規ページ追加
              </Button>

              <Box style={{ border: '1px solid #0000001A', borderRadius: 8, overflow: 'hidden', marginTop: 16 }}>
                <Table withColumnBorders styles={{ th: { backgroundColor: '#00000008' } }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={80}>ID</Table.Th>
                      <Table.Th>概要</Table.Th>
                      <Table.Th w={180}>開始日</Table.Th>
                      <Table.Th w={180}>終了日</Table.Th>
                      <Table.Th w={110} ta="center">プレビュー</Table.Th>
                      <Table.Th w={110} ta="center">ステータス</Table.Th>
                      <Table.Th w={80} ta="center">編集</Table.Th>
                      <Table.Th w={80} ta="center">削除</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredPages.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={8}>
                          <Text c="#00000099" ta="center" py="md">
                            {searchQuery ? '該当するページがありません' : 'ページが登録されていません'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      filteredPages.map((row) => (
                        <Table.Tr key={row.id}>
                          <Table.Td>{row.id}</Table.Td>
                          <Table.Td>{row.page_description}</Table.Td>
                          <Table.Td>{formatDate(row.start_date)}</Table.Td>
                          <Table.Td>{formatDate(row.end_date)}</Table.Td>
                          <Table.Td ta="center">
                            <Tooltip label="プレビュー">
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                component="a"
                                href={`/${client.url_name}/preview/${row.analytics_html}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <PlayCircleIcon size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                          <Table.Td ta="center">
                            <Badge color={row.enabled ? 'invy' : 'gray'} variant="light">
                              {row.enabled ? '公開中' : '非公開'}
                            </Badge>
                          </Table.Td>
                          <Table.Td ta="center">
                            <Tooltip label="編集">
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                onClick={() => handleClickEdit(row)}
                              >
                                <PencilSimpleIcon size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                          <Table.Td ta="center">
                            <Tooltip label={row.enabled ? '公開中のページは削除できません' : '削除'}>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                data-disabled={row.enabled || undefined}
                                onClick={(e) => {
                                  if (row.enabled) {
                                    e.preventDefault()
                                    return
                                  }
                                  handleClickDeleteButton(row.id)
                                }}
                              >
                                <TrashIcon size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              </Box>
            </Tabs.Panel>
          )
        })}
      </Tabs>

      {
        pageGroup && (
          <BaseModal
            opened={isDialogOpen}
            onClose={handleCloseDialog}
            title="ページの削除"
            width={600}
            size={400}
          >
            <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008">
              <Stack gap="sm" p="md">
                <Text>以下のページを削除します。よろしいですか？</Text>
                <Stack gap={4}>
                  <Text size="sm">ID: {pageGroup.id}</Text>
                  <Text size="sm">種類: {pageGroup.name}</Text>
                </Stack>
                <Text c="red" size="sm">※一度削除すると元に戻せません。</Text>
              </Stack>
            </ScrollArea>

            <Box
              p="md"
              style={{
                borderTop: '1px solid #0000001A',
                backgroundColor: '#fff',
                flexShrink: 0,
              }}
            >
              <Group justify="flex-end" gap={8}>
                <Button
                  variant="outline"
                  radius="xl"
                  color="gray"
                  onClick={handleCloseDialog}
                  disabled={deleting}
                >
                  キャンセル
                </Button>
                <Button
                  variant="filled"
                  radius="xl"
                  onClick={handleDeletePageGroup}
                  loading={deleting}
                >
                  削除
                </Button>
              </Group>
            </Box>
          </BaseModal>
        )
      }

      <PageGroupFormModal
        opened={openedForm}
        onClose={formModal.close}
        client={client}
        pageGroupId={editingId}
        onSaved={() => mutate()}
      />
    </Box >
  )
}

export default PageGroupsContainer
